import { useNavigate, useParams } from "react-router-dom";
import {
  NJCard,
  NJHeading,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { ChatWindow } from "@/features/chat/ChatWindow";
import { useChat } from "@/features/chat/ChatProvider";
import {
  useCloseSession,
  useDeleteSession,
  useReopenSession,
  useSessions,
} from "@/features/sessions/useSessions";
import type { Session } from "@/types";

function useAgentName(agentSlug: string): string {
  const { grantedAgents } = useChat();
  return (
    grantedAgents.find((agent) => agent.slug === agentSlug)?.name ?? agentSlug
  );
}

function ChatPanel({ session }: { session: Session }) {
  const navigate = useNavigate();
  const agentName = useAgentName(session.agentId);
  const closeSession = useCloseSession();
  const reopenSession = useReopenSession();
  const deleteSession = useDeleteSession();
  const isActive = session.status === "ACTIVE";

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-nj-neutral-subtle-default pb-3">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <NJHeading as="h1" scale="md">
              {agentName}
            </NJHeading>
            <NJTag
              label={isActive ? "Active" : "Closed"}
              variant={isActive ? "green" : "grey"}
            />
          </div>
          <NJText
            as="span"
            scale="sm"
            className="truncate text-nj-neutral-secondary-default"
          >
            {session.title}
          </NJText>
        </div>
        <div className="flex items-center gap-1">
          {isActive ? (
            <NJIconButton
              icon="lock"
              variant="tertiary"
              label="Close session"
              isDisabled={closeSession.isPending}
              onClick={() => closeSession.mutate(session.id)}
            />
          ) : (
            <NJIconButton
              icon="lock_open"
              variant="tertiary"
              label="Reopen session"
              isDisabled={reopenSession.isPending}
              onClick={() => reopenSession.mutate(session.id)}
            />
          )}
          <NJIconButton
            icon="delete"
            variant="destructive"
            label="Delete session"
            isDisabled={deleteSession.isPending}
            onClick={() => {
              if (window.confirm("Delete this conversation?")) {
                deleteSession.mutate(session.id, {
                  onSuccess: () => navigate("/chat"),
                });
              }
            }}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <ChatWindow session={session} agentName={agentName} />
      </div>
    </div>
  );
}

function RecentSessionCard({ session }: { session: Session }) {
  const navigate = useNavigate();
  const agentName = useAgentName(session.agentId);
  const isActive = session.status === "ACTIVE";
  return (
    <NJCard
      onClick={() => navigate(`/chat/${session.id}`)}
      className="!mb-0 flex w-full cursor-pointer flex-row items-center gap-4 p-4 transition hover:border-nj-brand-moderate-default hover:shadow-md"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <NJText as="span" scale="md" className="truncate font-nj-bold">
          {session.title}
        </NJText>
        <NJText
          as="span"
          scale="sm"
          className="truncate text-nj-neutral-secondary-default"
        >
          {agentName}
        </NJText>
      </div>
      <NJText
        as="span"
        scale="xs"
        className="hidden text-nj-neutral-tertiary-default sm:block"
      >
        Updated {new Date(session.updatedAt).toLocaleString()}
      </NJText>
      <NJTag
        label={isActive ? "Active" : "Closed"}
        variant={isActive ? "green" : "grey"}
        size="sm"
      />
    </NJCard>
  );
}

function RecentSessions({ sessions }: { sessions: Session[] }) {
  if (sessions.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <NJIcon name="chat" />
        <NJText
          as="p"
          scale="sm"
          className="max-w-sm text-nj-neutral-secondary-default"
        >
          Pick an agent below and send a message to start a new conversation.
        </NJText>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-4">
      <NJHeading as="h1" scale="lg">
        Recent sessions
      </NJHeading>
      <div className="flex w-full flex-col gap-3">
        {sessions.map((session) => (
          <RecentSessionCard key={session.id} session={session} />
        ))}
      </div>
    </div>
  );
}

/** Chat view — the conversation for `:sessionId`, or the "Recent sessions" list on `/chat`. The
 * composer is docked at the bottom of every page. */
export function ChatPage() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const { data: sessions, isLoading, isError } = useSessions();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <NJSpinner aria-label="Loading the conversation" />
      </div>
    );
  }

  if (isError) {
    return (
      <NJInlineMessage variant="error">
        Could not load the sessions.
      </NJInlineMessage>
    );
  }

  const session = sessionId
    ? (sessions ?? []).find((item) => item.id === sessionId)
    : undefined;

  if (sessionId && !session) {
    return (
      <NJInlineMessage variant="warning">
        This session no longer exists.
      </NJInlineMessage>
    );
  }

  return session ? (
    <ChatPanel session={session} />
  ) : (
    <RecentSessions sessions={sessions ?? []} />
  );
}
